import './CountrySelector.scss';

import { Content, Icon, Image, ListCell, ListView, Radio, RadioGroup, TextField, Typography } from '@modulor/react';
import type { CountryCode } from 'assets/countryDialCode';
import { countryDialCode } from 'assets/countryDialCode';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import type { FC, FormEvent } from 'react';
import { useMemo, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { countryCodeHandlerSelector } from 'store/slices/countryCodeState';
import { countryCodeSelector } from 'store/slices/global';
import { internalNavigateBack } from 'utils/navigationControl';

import { useNavigateWithSearch } from '../../hooks';
import { useSetHeaderConfig } from '../Header/hooks/useSetHeaderConfig';

const MIN_OPTIONS_COUNT_FOR_SEARCH_FIELD = 15;

export const CountrySelector: FC = () => {
  const navigate = useNavigateWithSearch();
  const setCountryCode = useAppSelector(countryCodeHandlerSelector);
  const countryCode = useAppSelector(countryCodeSelector);
  const { translate, skeletonT } = useSkeletonTranslation();
  const [searchCountry, setSearchCountry] = useState('');

  useSetHeaderConfig({
    preset: 'selectCountry',
    onNavigationAction: () => navigate(-1),
  });

  const displaySearchField = countryDialCode.length >= MIN_OPTIONS_COUNT_FOR_SEARCH_FIELD;

  const handleSearch = (value: string | FormEvent<HTMLDivElement>) => {
    if (typeof value === 'string') {
      setSearchCountry(value);
    }
  };

  const setCountryCodeHandler = (countryCodeParam: CountryCode) => {
    if (setCountryCode) {
      setCountryCode(countryCodeParam);
      internalNavigateBack(navigate);
    }
  };

  const filteredCountryDialCode = useMemo(
    () =>
      countryDialCode.filter(
        (country) =>
          country.nicename.toLowerCase().includes(searchCountry.toLowerCase()) ||
          country.phonecode.includes(searchCountry) ||
          `+${country.phonecode}`.includes(searchCountry),
      ),
    [searchCountry],
  );

  return (
    <>
      {displaySearchField && (
        <div className="field-wrapper search-country">
          <TextField
            placeholder={translate('PH.LISTCELL.SEARCH.TITLE')}
            type="text"
            value={searchCountry}
            onChange={handleSearch}
          />
        </div>
      )}
      {!filteredCountryDialCode.length && (
        <div className="country-selector-list empty-list-block">
          <Icon name="search" size={40} className="empty-list-block__icon" />
          <div className="empty-list-block__text">
            <Typography variant="title-2-semibold">{skeletonT('PH.LISTCELL.EMPTY.TITLE')}</Typography>
          </div>
        </div>
      )}
      <ListView className="country-selector-list">
        <RadioGroup onChange={() => {}}>
          {filteredCountryDialCode.map((country) => (
            <ListCell
              className="list-cell-control"
              text={`+${country.phonecode} ${country.nicename}`}
              key={country.id}
              icon={<Image name={country.iso} alt={country.nicename} decoding="async" loading="lazy" />}
              onClick={() => {
                setCountryCodeHandler(country);
              }}
              content={
                (
                  <Content>
                    <Radio value={country.iso} checked={country.iso === countryCode} />
                  </Content>
                ) as unknown as string
              }
            />
          ))}
        </RadioGroup>
      </ListView>
    </>
  );
};
